// apps/nos/client/src/services/ingredient-search_api.ts
import { serverURL } from "@/utils/serverURL";
import type {
  FamilyChip,
  IngredientFilters,
  IngredientPage,
  RoleCounts,
} from "@/utils/types/ingredient";

function buildQueryString(
  filters: Partial<IngredientFilters>,
  cursor: string | null = null,
  versionId: number | null = null,
  previewId: string | null = null,
): string {
  const p = new URLSearchParams();
  if (filters.kind && filters.kind !== "all") p.set("kind", filters.kind);
  if (filters.q && filters.q.trim())          p.set("q", filters.q.trim());
  if (filters.family)                         p.set("family", filters.family);
  if (filters.role)                           p.set("role", filters.role);
  if (cursor)                                 p.set("cursor", cursor);
  if (versionId != null)                      p.set("versionId", String(versionId));
  if (previewId != null)                      p.set("previewId", previewId);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

// All fetchers accept an optional `signal: AbortSignal`. React Query v5
// passes one to the queryFn automatically; when the query is canceled
// (queryKey change, component unmount, refetch) the signal aborts and the
// in-flight fetch is closed at the TCP level.
export async function fetchIngredientPage(
  flavorId: string,
  filters: IngredientFilters,
  cursor: string | null,
  signal?: AbortSignal,
  versionId: number | null = null,
  previewId: string | null = null,
): Promise<IngredientPage> {
  const qs = buildQueryString(filters, cursor, versionId, previewId);
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/ingredient-search${qs}`,
    { credentials: "include", signal },
  );
  if (!res.ok) throw new Error(`ingredient-search failed: ${res.status}`);
  return (await res.json()) as IngredientPage;
}

export async function fetchIngredientFamilies(
  flavorId: string,
  signal?: AbortSignal,
): Promise<{ families: FamilyChip[] }> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/ingredient-search/families`,
    { credentials: "include", signal },
  );
  if (!res.ok) throw new Error(`ingredient-search/families failed: ${res.status}`);
  return (await res.json()) as { families: FamilyChip[] };
}

export async function fetchIngredientRoleCounts(
  flavorId: string,
  filters: IngredientFilters,
  signal?: AbortSignal,
  versionId: number | null = null,
): Promise<RoleCounts> {
  // Despite the URL name, this endpoint returns all facet counts (role,
  // kind, family) plus the top-header total. Role is forwarded so the
  // server can compute the total matching every active filter; per-facet
  // counts internally exclude their own facet.
  //
  // versionId scopes the candidate set to the version's pool (FDA + cached)
  // so the totals/chips match the list endpoint exactly.
  const qs = buildQueryString(filters, null, versionId);
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/ingredient-search/role-counts${qs}`,
    { credentials: "include", signal },
  );
  if (!res.ok) throw new Error(`ingredient-search/role-counts failed: ${res.status}`);
  return (await res.json()) as RoleCounts;
}
